/**
 * Material Add to Design — ported from Designer
 * Handles adding materials to the SDS canvas
 *
 * The canvas receives the FULL-RESOLUTION image — it becomes the SDS
 * production material (low-res w=200 is for gallery browsing only).
 * Only accepts Cloudflare Images URLs; external URLs are proxied via SDS Worker.
 *
 * Module entry — inject orchestration + SDS URL proxy. Sibling
 * responsibilities: production-url.js (URL variant normalization),
 * material-size.js (picture_request gate + upscale), image-file.js
 * (JPEG conversion / URL-to-File utilities, re-exported here).
 */

import { store } from '../../../core/store.js'
import { CONFIG } from '../../../core/config.js'
import { eventBus, EVENTS } from '../../../core/event-bus.js'
import { createLogger } from '../../../utils/logger.js'
import { CF_IMAGES_DOMAIN, toProductionVariant } from './production-url.js'
import { ensureMaterialSize } from './material-size.js'
import { urlToFile } from './image-file.js'


// File-conversion utilities stay on this module's public surface.
export { urlToFile, dataUrlToFile } from './image-file.js'

const log = createLogger('add-to-design')

/**
 * Add material to design canvas
 */
export async function addMaterialToDesign(rawImageUrl, materialId, materialTitle, sizeHint) {
  const imageUrl = toProductionVariant(rawImageUrl)
  // Enforce SDS picture_request material-size standard before inject. The image
  // injected into the canvas IS the print material, so an under-size source
  // yields an unorderable finished product (sds-data-model.md §5/§6). Returns
  // EITHER a url to inject as-is OR a pre-built size-controlled JPEG File (when
  // the original was upscaled via aura-sr).
  const prepared = await ensureMaterialSize(imageUrl, sizeHint)
  const preparedFile = prepared.file ?? null
  const injectUrl = prepared.url ?? imageUrl
  // Track used materials
  if (materialId && window.MaterialManager?.state?.materials) {
    const matState = window.MaterialManager.state
    const material = matState.materials.find(m => String(m.id) === String(materialId))
    if (material) {
      const alreadyUsed = matState.usedMaterials.some(m => String(m.id) === String(materialId))
      if (!alreadyUsed) {
        const usedEntry = {
          id: material.id,
          url: material.url,
          thumbnail_url: material.thumbnail_url,
          spu: material.spu || material.id,
          artist_id: material.artist_id || material.customer_id,
          title: material.title,
        }
        matState.usedMaterials.push(usedEntry)

        try {
          const recent = JSON.parse(localStorage.getItem('mms_recent_materials') || '[]')
          if (!recent.some(m => String(m.id) === String(materialId))) {
            recent.unshift(usedEntry)
            if (recent.length > 20) recent.length = 20
            localStorage.setItem('mms_recent_materials', JSON.stringify(recent))
          }
        } catch (e) { /* ignore */ }
      }
    }
  }

  const sdk = store.getState('design.sdkInstance')
  const isReady = store.getState('design.isSDKReady')

  if (!sdk || !isReady) {
    eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Designer not ready yet, please wait', type: 'warning' })
    log.warn('Designer not ready')
    return
  }

  eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Adding image to design...', type: 'info', duration: 2000 })

  try {
    const fileName = 'design-img-' + Date.now() + '.jpg'

    let file
    if (preparedFile) {
      // ensureMaterialSize already produced a size-controlled JPEG (upscale
      // path) — inject it directly, no proxy / re-fetch needed.
      file = preparedFile
    } else {
      // Proxy genuinely-external URLs to avoid CORS. Our own CDN delivery
      // hosts are CORS-friendly and must NOT take the proxy hop (the old
      // check only knew imagedelivery.net, so cdn.mamamiya.store URLs hit
      // the proxy endpoint — which 404s, the legacy convert-image-url route
      // was never ported — before falling back to direct).
      const isOwnCdn =
        injectUrl.includes(CF_IMAGES_DOMAIN) ||
        injectUrl.includes('cdn.mamamiya.store/cdn-cgi/imagedelivery/')
      let fetchUrl = injectUrl
      if (!isOwnCdn) {
        log.info('External URL, using SDS proxy...')
        try {
          fetchUrl = await convertToSDSUrlViaWorker(injectUrl)
        } catch (proxyError) {
          log.warn('Proxy failed, trying direct:', proxyError.message)
        }
      }
      file = await urlToFile(fetchUrl, fileName)
    }
    log.info('File ready:', { name: file.name, size: file.size, type: file.type })

    if (!sdk.sarto) throw new Error('SDK sarto not available')

    return new Promise((resolve, reject) => {
      // 90s: a production-resolution material (multi-MP JPEG) takes longer than
      // the old 30s for the SDK's internal SDS upload. why → SDS SDK timeout on
      // large material.
      const timeout = setTimeout(() => {
        log.error('addSDKImage timeout (90s)')
        reject(new Error('Add image timeout (90s)'))
      }, 90000)

      sdk.sarto.addSDKImage(file, (result) => {
        clearTimeout(timeout)
        if (!result) {
          reject(new Error('No result from addSDKImage'))
          return
        }
        if (result.status === 'success') {
          log.info('Image added to canvas')
          eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Image added to design!', type: 'success' })
          eventBus.emit(EVENTS.LAYER_ADD, { materialId, materialTitle })
          resolve({ status: 'success' })
        } else {
          reject(new Error(result.message || 'Failed to add image'))
        }
      })
    })
  } catch (error) {
    log.error('Failed to add material:', error)

    let errorMessage = 'Failed to add image'
    if (error.message.includes('Failed to fetch')) errorMessage = 'Network error: Cannot load image'
    else if (error.message.includes('CORS')) errorMessage = 'CORS error: Image blocked'
    else if (error.message.includes('HTTP')) errorMessage = 'Server error: ' + error.message
    else errorMessage = error.message

    eventBus.emit(EVENTS.TOAST_SHOW, { message: errorMessage, type: 'error' })
    throw error
  }
}

/**
 * Convert an external image URL into an SDS-hosted URL via the SDS standard
 * upload endpoint. Per sds-data-model.md §3/§6, EVERY image submitted to an SDS
 * business endpoint must first be turned into an SDS url through
 * POST /gateway/images/url ({url} -> {file_code, url}). CONFIG.SDS_API points at
 * admin-api, whose /gateway/* proxy transparently forwards to
 * merchantapi.sdspod.com/gateway/images/url (sdk.ts createSdkGatewayProxyRoutes).
 *
 * The old /api/convert-image-url route was never ported (404) — this is the
 * replacement. Returns the SDS-hosted url.
 *
 * @param {string} externalUrl
 * @returns {Promise<string>}
 */
export async function convertToSDSUrlViaWorker(externalUrl) {
  const response = await fetch(`${CONFIG.SDS_API}/gateway/images/url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url: externalUrl }),
    signal: AbortSignal.timeout(15000),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
  }

  const result = await response.json()
  // SDS returns { file_code, url } per §3. Some gateway responses wrap the
  // payload in a data envelope — accept both shapes; the SDS url is what we
  // inject (file_code retained for diagnostics only).
  const sdsUrl = result?.url || result?.data?.url
  if (!sdsUrl) {
    throw new Error(result?.error || result?.message || 'No SDS url in response')
  }
  return sdsUrl
}
